package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.WebIconSize
import com.merkost.handpass.sharedweb.theme.WebIconStroke
import com.varabyte.kobweb.compose.dom.svg.Circle
import com.varabyte.kobweb.compose.dom.svg.Line
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Rect
import com.varabyte.kobweb.compose.dom.svg.SVGFillType
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineCap
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineJoin
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeType
import com.varabyte.kobweb.compose.dom.svg.Svg

sealed interface IconShape {
    data class Path(val d: String) : IconShape
    data class Circle(val cx: Number, val cy: Number, val r: Number) : IconShape
    data class Rect(
        val x: Number,
        val y: Number,
        val width: Number,
        val height: Number,
        val rx: Number? = null,
    ) : IconShape
    data class Line(val x1: Number, val y1: Number, val x2: Number, val y2: Number) : IconShape
}

enum class WebIcon(val shapes: List<IconShape>, val filled: Boolean = false) {
    Queue(
        listOf(
            IconShape.Path("M22 12h-6l-2 3h-4l-2-3H2"),
            IconShape.Path("M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"),
        ),
    ),
    Users(
        listOf(
            IconShape.Path("M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"),
            IconShape.Circle(9, 7, 4),
            IconShape.Path("M22 21v-2a4 4 0 0 0-3-3.87"),
            IconShape.Path("M16 3.13a4 4 0 0 1 0 7.75"),
        ),
    ),
    Listings(
        listOf(
            IconShape.Rect(3, 3, 7, 7, 1),
            IconShape.Rect(14, 3, 7, 7, 1),
            IconShape.Rect(14, 14, 7, 7, 1),
            IconShape.Rect(3, 14, 7, 7, 1),
        ),
    ),
    Logs(
        listOf(
            IconShape.Path("M15 12h-5"),
            IconShape.Path("M15 8h-5"),
            IconShape.Path("M19 17V5a2 2 0 0 0-2-2H4"),
            IconShape.Path("M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"),
        ),
    ),
    Stats(
        listOf(
            IconShape.Path("M3 3v18h18"),
            IconShape.Path("M18 17V9"),
            IconShape.Path("M13 17V5"),
            IconShape.Path("M8 17v-3"),
        ),
    ),
    Search(
        listOf(
            IconShape.Circle(11, 11, 8),
            IconShape.Path("m21 21-4.3-4.3"),
        ),
    ),
    Check(listOf(IconShape.Path("M20 6 9 17l-5-5"))),
    Close(
        listOf(
            IconShape.Path("M18 6 6 18"),
            IconShape.Path("m6 6 12 12"),
        ),
    ),
    Ban(
        listOf(
            IconShape.Circle(12, 12, 10),
            IconShape.Path("m4.9 4.9 14.2 14.2"),
        ),
    ),
    Sun(
        listOf(
            IconShape.Circle(12, 12, 4),
            IconShape.Path("M12 2v2"),
            IconShape.Path("M12 20v2"),
            IconShape.Path("m4.93 4.93 1.41 1.41"),
            IconShape.Path("m17.66 17.66 1.41 1.41"),
            IconShape.Path("M2 12h2"),
            IconShape.Path("M20 12h2"),
            IconShape.Path("m6.34 17.66-1.41 1.41"),
            IconShape.Path("m19.07 4.93-1.41 1.41"),
        ),
    ),
    Moon(listOf(IconShape.Path("M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"))),
    Monitor(
        listOf(
            IconShape.Rect(2, 3, 20, 14, 2),
            IconShape.Path("M8 21h8"),
            IconShape.Path("M12 17v4"),
        ),
    ),
    Bell(
        listOf(
            IconShape.Path("M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"),
            IconShape.Path("M10.3 21a1.94 1.94 0 0 0 3.4 0"),
        ),
    ),
    Info(
        listOf(
            IconShape.Circle(12, 12, 10),
            IconShape.Path("M12 16v-4"),
            IconShape.Path("M12 8h.01"),
        ),
    ),
    Alert(
        listOf(
            IconShape.Circle(12, 12, 10),
            IconShape.Path("M12 8v4"),
            IconShape.Path("M12 16h.01"),
        ),
    ),
    Refresh(
        listOf(
            IconShape.Path("M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"),
            IconShape.Path("M3 3v5h5"),
            IconShape.Path("M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"),
            IconShape.Path("M21 21v-5h-5"),
        ),
    ),
    Plus(
        listOf(
            IconShape.Path("M5 12h14"),
            IconShape.Path("M12 5v14"),
        ),
    ),
    Eye(
        listOf(
            IconShape.Path("M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"),
            IconShape.Circle(12, 12, 3),
        ),
    ),
    Menu(
        listOf(
            IconShape.Line(4, 6, 20, 6),
            IconShape.Line(4, 12, 20, 12),
            IconShape.Line(4, 18, 20, 18),
        ),
    ),
    Sliders(
        listOf(
            IconShape.Line(4, 6, 14, 6),
            IconShape.Circle(17, 6, 2),
            IconShape.Line(19, 6, 20, 6),
            IconShape.Line(4, 12, 8, 12),
            IconShape.Circle(11, 12, 2),
            IconShape.Line(14, 12, 20, 12),
            IconShape.Line(4, 18, 15, 18),
            IconShape.Circle(18, 18, 2),
            IconShape.Line(20, 18, 20, 18),
        ),
    ),
    ChevronLeft(listOf(IconShape.Path("m15 18-6-6 6-6"))),
    ChevronRight(listOf(IconShape.Path("m9 18 6-6-6-6"))),
    MapPin(
        listOf(
            IconShape.Path("M12 21c4-4 7-7.5 7-11a7 7 0 1 0-14 0c0 3.5 3 7 7 11Z"),
            IconShape.Circle(12, 10, 2.5),
        ),
    ),
    Star(listOf(IconShape.Path(STAR_PATH))),
    StarFilled(listOf(IconShape.Path(STAR_PATH)), filled = true),
    CheckCircle(
        listOf(
            IconShape.Circle(12, 12, 9),
            IconShape.Path("M8.5 12l2.5 2.5 4.5-5"),
        ),
    ),
    Package(
        listOf(
            IconShape.Path("M21 8l-9-5-9 5v8l9 5 9-5V8Z"),
            IconShape.Path("M3 8l9 5 9-5"),
            IconShape.Path("M12 13v9"),
        ),
    ),
    Camera(
        listOf(
            IconShape.Path("M3 8h3l2-3h8l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"),
            IconShape.Circle(12, 13, 3.5),
        ),
    ),
    ChatCircle(listOf(IconShape.Path("M4 18l1.2-3A8 8 0 1 1 9 19.5L4 18Z"))),
    ShieldCheck(
        listOf(
            IconShape.Path("M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z"),
            IconShape.Path("M9 12l2 2 4-4.5"),
        ),
    ),
    Handshake(
        listOf(
            IconShape.Path("M3 8l5-4 4 3 4-3 5 4"),
            IconShape.Path("M3 8v8l5 4 4-3 4 3 5-4V8"),
            IconShape.Path("M8 12l3 3 3-3"),
        ),
    ),
    Bolt(listOf(IconShape.Path("M13 2L4 14h7l-2 8 9-12h-7l2-8Z"))),
    Bookmark(listOf(IconShape.Path(BOOKMARK_PATH))),
    // Same geometry as the app's HandPassIcons.Share01 — the item lifts out of the tray.
    Share(
        listOf(
            IconShape.Path("M21 12v4.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 21 17.88 21 16.2 21H7.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C3 18.72 3 17.88 3 16.2V12"),
            IconShape.Path("M16 7l-4-4-4 4"),
            IconShape.Path("M12 3v11.5"),
        ),
    ),
    BookmarkFilled(listOf(IconShape.Path(BOOKMARK_PATH)), filled = true),
    CaretDown(listOf(IconShape.Path("M6 9l6 6 6-6"))),
    CaretRight(listOf(IconShape.Path("M9 6l6 6-6 6"))),
    ArrowRight(
        listOf(
            IconShape.Path("M4 12h16"),
            IconShape.Path("M14 6l6 6-6 6"),
        ),
    ),
    Globe(
        listOf(
            IconShape.Circle(12, 12, 9),
            IconShape.Path("M3 12h18"),
            IconShape.Path("M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9s1.3-6.5 3.8-9Z"),
        ),
    ),
    Calendar(
        listOf(
            IconShape.Rect(4, 5, 16, 15, 1),
            IconShape.Path("M4 9h16"),
            IconShape.Path("M8 3v4"),
            IconShape.Path("M16 3v4"),
        ),
    ),
    Lock(
        listOf(
            IconShape.Rect(5, 11, 14, 9, 1),
            IconShape.Path("M8 11V8a4 4 0 0 1 8 0v3"),
        ),
    ),
    Tag(
        listOf(
            IconShape.Path("M3 12.5V4a1 1 0 0 1 1-1h8.5a1 1 0 0 1 .7.3l7 7a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-7-7a1 1 0 0 1-.3-.7Z"),
            IconShape.Path("M7.5 7.5h.01"),
        ),
    ),
    Verified(
        listOf(
            IconShape.Circle(12, 12, 9),
            IconShape.Path("M8.5 12l2.5 2.5 4.5-5"),
        ),
    ),
    Crosshair(
        listOf(
            IconShape.Circle(12, 12, 7),
            IconShape.Path("M12 2v3"),
            IconShape.Path("M12 19v3"),
            IconShape.Path("M2 12h3"),
            IconShape.Path("M19 12h3"),
            IconShape.Circle(12, 12, 1.5),
        ),
    ),
    Clock(
        listOf(
            IconShape.Circle(12, 12, 9),
            IconShape.Path("M12 7v5l3.5 2"),
        ),
    ),
}

private const val STAR_PATH =
    "M12 3l2.7 5.5 6 .9-4.35 4.25 1 6-5.35-2.8-5.35 2.8 1-6L3.3 9.4l6-.9L12 3Z"

// Saving a listing is bookmarking it to come back to, not liking it. Same geometry as the app's
// HandPassIcons.Bookmark so the two surfaces show one glyph, not two dialects of one.
private const val BOOKMARK_PATH =
    "M5 7.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C7.28 3 8.12 3 9.8 3h4.4c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C19 5.28 19 6.12 19 7.8V21l-7-4-7 4V7.8Z"

@Composable
fun Icon(
    icon: WebIcon,
    size: Int = WebIconSize.Md,
    color: String? = null,
    strokeWidth: Double = WebIconStroke.Default,
    ariaHidden: Boolean = true,
) {
    Svg(attrs = {
        viewBox(0, 0, 24, 24)
        width(size)
        height(size)
        fill(if (icon.filled) SVGFillType.CurrentColor else SVGFillType.None)
        stroke(SVGStrokeType.CurrentColor)
        strokeWidth(strokeWidth)
        strokeLineCap(SVGStrokeLineCap.Round)
        strokeLineJoin(SVGStrokeLineJoin.Round)
        if (ariaHidden) attr("aria-hidden", "true")
        style {
            property("display", "block")
            property("flex-shrink", "0")
            color?.let { property("color", it) }
        }
    }) {
        icon.shapes.forEach { shape ->
            when (shape) {
                is IconShape.Path -> Path { d(shape.d) }
                is IconShape.Circle -> Circle {
                    cx(shape.cx)
                    cy(shape.cy)
                    r(shape.r)
                }
                is IconShape.Rect -> Rect {
                    x(shape.x)
                    y(shape.y)
                    width(shape.width)
                    height(shape.height)
                    shape.rx?.let { rx(it) }
                }
                is IconShape.Line -> Line {
                    x1(shape.x1)
                    y1(shape.y1)
                    x2(shape.x2)
                    y2(shape.y2)
                }
            }
        }
    }
}
